import { Box, Button, Chip, Divider, FormControlLabel, IconButton, InputAdornment, Stack, styled, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { useState } from "react";
import { CustomTooltip } from "src/content/keywordResearch/CustomTooltip";
import { keywordResearchDashBoardCss } from "src/content/keywordResearch/keywordResearchCSS";
import CloseIcon from '@mui/icons-material/Close';


const CustomSwitch = styled(Switch)(() => {
  let activeColor = 'var(--color-secondary)';

  return {
    width: 48,
    height: 26,
    padding: 0,
    display: 'flex',
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'auto',
    },
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 3,
      transitionDuration: '300ms',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
        '& .MuiSwitch-input': {
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        }
      },
      '&.Mui-checked': {
        transform: 'translateX(22px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: activeColor,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 20,
      height: 20,
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
      backgroundColor: 'white',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
      },
    },
    '& .MuiSwitch-track': {
      borderRadius: 13,
      backgroundColor: 'var(--color-bg-mid-grey)',
      opacity: 1,
      transition: 'background-color 300ms',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
      },
    },
  };
});

const tones = [
  {
    value: "professional",
    label: "Professional"
  },
  {
    value: "friendly",
    label: "Friendly",
  },
  {
    value: "warm",
    label: "Warm",
  },
  {
    value: "formal",
    label: "Formal",
  },
  {
    value: "casual",
    label: "Casual",
  },
  {
    value: "enthusiastic",
    label: "Enthusiastic",
  },
];

const toneButtonSx = {
  textTransform: "none",
  minWidth: 128,
  height: 32,
  px: 2,
  borderRadius: "4px !important",
  border: "none",
  color: "#1A0050",
  fontWeight: 400,
  fontSize: 14,

  "&.Mui-selected": {
    backgroundColor: "#EDE9FE",
    color: "#1A0050",
    fontWeight: 600,
  },

  "&:hover": {
    backgroundColor: "rgba(237,233,254,.6)",
  },
};

export interface ReviewAutomationSettings {
  ai_instructions: string;
  tone: string;
  banned_phrases: string[];
  optionalControls: {
    personalize_with_name: boolean;
    skip_5_star: boolean;
    skip_short_reviews: boolean;
    reply_only_with_text: boolean;
    require_approval_for_1_2_star: boolean;
  };
}

export const defaultSettings: ReviewAutomationSettings = {
  ai_instructions: "",
  tone: "professional",
  banned_phrases: [],
  optionalControls: {
    personalize_with_name: true,
    skip_5_star: false,
    skip_short_reviews: false,
    reply_only_with_text: false,
    require_approval_for_1_2_star: true,
  },
};

export const defaultPayloadSettings = {
  ai_instructions: defaultSettings.ai_instructions,
  tone: defaultSettings.tone,
  banned_phrases: defaultSettings.banned_phrases,
  ...defaultSettings.optionalControls,
};

interface SettingsFormProps {
  settings: ReviewAutomationSettings;
  onChange: React.Dispatch<React.SetStateAction<ReviewAutomationSettings>>;
}

const optionalControls = [
  {
    id: "personalize_with_name" as const,
    label: "Personalize Replies Using Reviewer Name",
  },
  {
    id: "skip_5_star" as const,
    label: "Skip 5-Star Reviews",
  },
  {
    id: "skip_short_reviews" as const,
    label: "Skip Short Reviews",
  },
  {
    id: "reply_only_with_text" as const,
    label: "Reply Only To Reviews With Text",
  },
  {
    id: "require_approval_for_1_2_star" as const,
    label: "Require Approval For 1–2 Star Reviews",
  },
];

function SettingsForm({ settings, onChange }: SettingsFormProps) {
  const [newTopic, setNewTopic] = useState("");

  const handleAddTopic = () => {
    const trimmed = newTopic.trim();
    if (
      trimmed &&
      !settings.banned_phrases.some(
        (phrase) => phrase.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      onChange((prev) => ({
        ...prev,
        banned_phrases: [...prev.banned_phrases, trimmed],
      }));
      setNewTopic("");
    }
  };

  const handleRemoveTopic = (indexToRemove: number) => {
    onChange((prev) => ({
      ...prev,
      banned_phrases: prev.banned_phrases.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleChangeTone = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      onChange((prev) => ({ ...prev, tone: newValue }));
    }
  };

  return (
    <div className="mt-3">
      <div>
        <Typography variant="body2" sx={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>
          AI Instruction
        </Typography>
        <div className=" flex justify-between items-center mt-1 text-[#344054] ">
          <TextField
            multiline
            className="w-full"
            rows={2}
            value={settings.ai_instructions}
            onChange={(e) =>
              onChange((prev) => ({ ...prev, ai_instructions: e.target.value }))
            }
            placeholder="e.g., Always thank customers for positive feedback, keep responses concise, and direct support inquiries to contact@example.com..."
          />
        </div>

        {/**Tone */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            mb: 1,
            fontWeight: 500,
            color: 'var(--color-text-mid)',
          }}
        >
          Tone
        </Typography>

        <ToggleButtonGroup
          exclusive
          value={settings.tone}
          onChange={handleChangeTone}
          sx={{
            display: "inline-flex",
            width: "100%",
            border: "1px solid var(--color-border-neutral)",
            borderRadius: "8px",
            p: 0.5,
            gap: 0.5,
            overflowX: "auto", // only if needed
            whiteSpace: "nowrap",

            "& .MuiToggleButton-root": {
              flex: "1 1 auto",
              minWidth: 0,
              px: {
                xs: 1,
                sm: 1.5,
                md: 2,
              },
            },
          }}
        >
          {tones.map((tone) => (
            <ToggleButton
              key={tone.value}
              value={tone.value}
              sx={{
                ...toneButtonSx,
                whiteSpace: "nowrap",
              }}
            >
              <span>{tone.label}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Keywords/Pharases to Avoid */}
        <Box mt={3}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'var(--color-text-mid)',
            }}
          >
            Keywords / Phrases To Avoid
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <TextField
              fullWidth
              placeholder="Enter topic"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddTopic();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 42,
                  borderRadius: "8px",
                },
              }}
            />

            <Button
              variant="contained"
              onClick={handleAddTopic}
              sx={{
                minWidth: 100,
                height: 42,
                borderRadius: "8px",
                textTransform: "none",
              }}
            >
              Add
            </Button>
          </Stack>

          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ gap: 1, mt: 1 }}
          >
            {settings.banned_phrases.map((topic, index) => (
              <Chip
                key={`${topic}-${index}`}
                label={topic}
                onDelete={() => handleRemoveTopic(index)}
                sx={{
                  fontWeight: 500,
                  borderRadius: 5,
                  backgroundColor: "#D0F7EF",
                  "& .MuiChip-deleteIcon": {
                  },
                }}
                deleteIcon={
                  <CloseIcon
                    sx={{ color: 'var(--color-text-dark) !important' }}
                  />
                }
              />
            ))}
          </Stack>
        </Box>

        <Box mt={3}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 500,
              color: 'var(--color-text-mid)',
            }}
          >
            Optional Controls
          </Typography>

          <Stack divider={<Divider />}>
            {optionalControls.map((item) => (
              <Stack
                key={item.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={0.5}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  {item.label}
                </Typography>

                <CustomSwitch
                  checked={settings.optionalControls[item.id]}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      optionalControls: {
                        ...prev.optionalControls,
                        [item.id]: e.target.checked,
                      },
                    }))
                  }
                />
              </Stack>
            ))}
          </Stack>
        </Box>
      </div>
    </div>
  )
}

export default SettingsForm